// src/pages/admin/AdminSettings.tsx
import { useEffect, useState } from "react";
import {
  useGetAdminMeQuery,
  useAdminChangePasswordMutation,
} from "../../app/api/adminAuthApi";
import { useSelector } from "react-redux";
import type { RootState } from "../../app/store";
import useHashScroll from "../../app/useHashScroll";
import { PageHeader, Button, Avatar, Skeleton, formatNaira } from "./AdminShared";
import {
  useGetPricingQuery,
  useUpdatePricingMutation,
  useRunLearningJobMutation,
} from "../../app/api/libraryApi";
import type {
  DeliveryMode,
  LearningJobReport,
  LibraryTrack,
  PricingData,
} from "../../app/api/libraryApi";

import { Check } from "lucide-react";
const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  staff: "Staff",
};

/* ─── Profile section ────────────────────────────────────────────── */
function ProfileSection() {
  const { data, isLoading } = useGetAdminMeQuery();
  const user = data?.data;

  if (isLoading)
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 rounded-xl" />
        ))}
      </div>
    );
  if (!user) return null;

  const inputCls =
    "w-full rounded-xl border border-[#dde2ec] bg-[#f9fafc] px-3 py-2.5 text-sm text-[#5a6278] outline-none cursor-default";

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6 space-y-5">
      <div className="flex items-center gap-4">
        <Avatar firstName={user.firstName} lastName={user.lastName} size="lg" />
        <div>
          <p className="text-base font-bold text-[#16213e]">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-xs text-[#8189a0]">{user.email}</p>
          <span className="mt-1 inline-flex items-center rounded-full bg-[#ebeff7] px-2.5 py-0.5 text-[12.5px] font-bold text-[#4a44b0]">
            {ROLE_LABELS[user.role] ?? user.role}
          </span>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            First name
          </label>
          <input
            readOnly
            value={user.firstName}
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Last name
          </label>
          <input
            readOnly
            value={user.lastName}
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Email address
          </label>
          <input readOnly value={user.email} className={inputCls + " mt-1.5"} />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Account role
          </label>
          <input
            readOnly
            value={ROLE_LABELS[user.role] ?? user.role}
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Email verified
          </label>
          <input
            readOnly
            value={user.isEmailVerified ? "Yes" : "No"}
            className={inputCls + " mt-1.5"}
          />
        </div>
      </div>
      <p className="text-[11.5px] text-[#a6adbf]">
        Profile fields are managed by a super admin. Contact them to update your
        name or email.
      </p>
    </div>
  );
}

/* ─── Change password section ────────────────────────────────────── */
function ChangePasswordSection() {
  const [changePassword, { isLoading }] = useAdminChangePasswordMutation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    if (!current || !next || !confirm) {
      setError("All fields are required");
      return;
    }
    if (next.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    try {
      await changePassword({
        currentPassword: current,
        newPassword: next,
      }).unwrap();
      setSuccess("Password changed successfully.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e: any) {
      setError(e?.data?.error ?? "Failed to change password");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]";

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6 space-y-4">
      <div>
        <h3 className="text-base font-bold text-[#16213e]">Change password</h3>
        <p className="text-xs text-[#8189a0] mt-0.5">
          Choose a strong password of at least 8 characters.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Current password
          </label>
          <div className="relative mt-1.5">
            <input
              type={showPwd ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputCls + " pr-10"}
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[11.5px] font-bold text-[#8189a0] hover:text-[#3b4560]"
            >
              {showPwd ? "HIDE" : "SHOW"}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            New password
          </label>
          <input
            type={showPwd ? "text" : "password"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Confirm new password
          </label>
          <input
            type={showPwd ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputCls + " mt-1.5"}
          />
        </div>
      </div>
      {error && <p className="text-xs text-[#e57373]">{error}</p>}
      {success && (
        <p className="text-xs font-semibold text-[#6cc2a0]"><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{success}</p>
      )}
      <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
        {isLoading ? "Changing…" : "Change password"}
      </Button>
    </div>
  );
}

/* ─── Academy info section (static display) ─────────────────────── */
function AcademyInfoSection() {
  const items = [
    { label: "Academy name", value: "Future Smart Lab Innovation Academy" },
    {
      label: "Delivery",
      value: "Online now · Hybrid and on-site opening cluster by cluster",
    },
    {
      label: "Mission",
      value:
        "To raise children who are prepared for prosperity and innovation and equipped to solve tomorrow's problems.",
    },
    {
      label: "Tracks",
      value:
        "Foundation Builders (6–9) · Explorers (9–12) · Innovators (13–18)",
    },
    {
      label: "Billing periods",
      value: "Monthly · Quarterly (5% off) · Annually (10% off)",
    },
  ];
  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6 space-y-4">
      <div>
        <h3 className="text-base font-bold text-[#16213e]">
          Academy information
        </h3>
        <p className="text-xs text-[#8189a0] mt-0.5">
          Reference information for this academy instance.
        </p>
      </div>
      <div className="space-y-3">
        {items.map((item: { label: string; value: string }) => (
          <div
            key={item.label}
            className="rounded-[14px] border border-[#ebeff7] bg-[#f9fafc] px-4 py-3"
          >
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#8189a0]">
              {item.label}
            </p>
            <p className="mt-0.5 text-xs font-semibold text-[#16213e] leading-5">
              {item.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Learning modes & prices ────────────────────────────────────── */
const PRICE_TRACKS: { track: LibraryTrack; label: string }[] = [
  { track: "foundation_builders", label: "Foundation Builders (6–9)" },
  { track: "explorers", label: "Explorers (9–12)" },
  { track: "innovators", label: "Innovators (13–18)" },
];
const MODES: { mode: DeliveryMode; label: string }[] = [
  { mode: "online", label: "Online" },
  { mode: "hybrid", label: "Hybrid" },
  { mode: "onsite", label: "On-site" },
];

function PricingSection() {
  const me = useSelector((s: RootState) => s.adminAuth.user);
  const canEdit = me?.role === "admin" || me?.role === "super_admin";
  const { data, isLoading } = useGetPricingQuery();
  const [update, { isLoading: saving }] = useUpdatePricingMutation();
  const [draft, setDraft] = useState<PricingData | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (data?.data) setDraft(data.data);
  }, [data]);

  if (isLoading || !draft)
    return <Skeleton className="h-64 rounded-[22px]" />;

  const setFee = (t: LibraryTrack, m: DeliveryMode, v: string) =>
    setDraft({
      ...draft,
      fees: {
        ...draft.fees,
        [t]: { ...draft.fees[t], [m]: v === "" ? null : Number(v) },
      },
    });
  const toggleMode = (m: DeliveryMode) =>
    setDraft({
      ...draft,
      openModes: draft.openModes.includes(m)
        ? draft.openModes.filter((x) => x !== m)
        : [...draft.openModes, m],
    });

  const onSave = async () => {
    setMsg(null);
    try {
      await update({
        fees: draft.fees,
        openModes: draft.openModes,
        onsiteClusterThreshold: draft.onsiteClusterThreshold,
      }).unwrap();
      setMsg({ ok: true, text: "Prices saved. Parents see the new prices straight away." });
    } catch (err: any) {
      setMsg({ ok: false, text: err?.data?.error ?? "Could not save" });
    }
  };

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6 space-y-4">
      <div>
        <h3 className="text-base font-bold text-[#16213e]">Learning modes & prices</h3>
        <p className="text-xs text-[#8189a0] mt-0.5">
          Monthly fee per age group and mode, before the quarterly (5%) and annual (10%) discounts.
          A closed mode shows to parents as “Coming soon”.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="pb-2 text-left text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">Age group</th>
              {MODES.map(({ mode, label }) => (
                <th key={mode} className="pb-2 text-left text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PRICE_TRACKS.map(({ track, label }) => (
              <tr key={track} className="border-t border-[#ebeff7]">
                <td className="py-2 pr-3 font-semibold text-[#16213e]">{label}</td>
                {MODES.map(({ mode }) => (
                  <td key={mode} className="py-2 pr-2">
                    {canEdit ? (
                      <input
                        type="number"
                        min={0}
                        step={500}
                        value={draft.fees[track][mode] ?? ""}
                        placeholder="Not set"
                        onChange={(e) => setFee(track, mode, e.target.value)}
                        className="w-24 rounded-lg border border-[#dde2ec] px-2 py-1.5 text-xs outline-none focus:border-[#1b2d5e]"
                      />
                    ) : (
                      <span>{formatNaira(draft.fees[track][mode])}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-2">
        {MODES.map(({ mode, label }) => {
          const open = draft.openModes.includes(mode);
          return (
            <button
              key={mode}
              disabled={!canEdit}
              onClick={() => toggleMode(mode)}
              className={`rounded-full px-3 py-1.5 text-[12.5px] font-bold transition ${open ? "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]" : "bg-[#f1f3f8] text-[#8189a0] border border-[#e5e8ef]"}`}
            >
              {label}: {open ? "Open" : "Coming soon"}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <label className="text-xs text-[#3b4560]">Open on-site in a cluster at</label>
        <input
          type="number"
          min={1}
          disabled={!canEdit}
          value={draft.onsiteClusterThreshold}
          onChange={(e) => setDraft({ ...draft, onsiteClusterThreshold: Number(e.target.value) })}
          className="w-16 rounded-lg border border-[#dde2ec] px-2 py-1.5 text-xs outline-none focus:border-[#1b2d5e]"
        />
        <span className="text-xs text-[#3b4560]">active children</span>
      </div>

      {msg && (
        <p className={`text-xs font-semibold ${msg.ok ? "text-[#2a7a56]" : "text-[#b94a3a]"}`}>{msg.text}</p>
      )}
      {canEdit ? (
        <Button onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save prices"}</Button>
      ) : (
        <p className="text-[12.5px] text-[#8189a0]">Only admins can change prices.</p>
      )}
    </div>
  );
}

/* ─── Daily learning job ─────────────────────────────────────────── */
function LearningJobSection() {
  const me = useSelector((s: RootState) => s.adminAuth.user);
  const canRun = me?.role === "admin" || me?.role === "super_admin";
  const [run, { isLoading }] = useRunLearningJobMutation();
  const [report, setReport] = useState<LearningJobReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const go = async (dryRun: boolean) => {
    if (!dryRun && !window.confirm("Send today's weekly summaries and behind-target alerts now?")) return;
    setError(null);
    try {
      const res = await run({ dryRun }).unwrap();
      setReport(res.data);
    } catch {
      setError("Could not run the job. Try again.");
    }
  };

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6 space-y-4">
      <div>
        <h3 className="text-base font-bold text-[#16213e]">Weekly summaries &amp; alerts</h3>
        <p className="text-xs text-[#8189a0] mt-0.5">
          Runs automatically every morning. Each online family gets one summary when their child&apos;s
          learning week rolls over, and an alert when a child is 2+ weeks behind (copied to the tutor who
          last reviewed their work, or to Messages).
        </p>
      </div>
      {canRun ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={() => go(true)} disabled={isLoading}>
            {isLoading ? "Working…" : "Preview today"}
          </Button>
          <Button size="sm" onClick={() => go(false)} disabled={isLoading}>
            Send now
          </Button>
        </div>
      ) : (
        <p className="text-[12.5px] text-[#8189a0]">Only admins can run the job.</p>
      )}
      {error && <p className="text-xs font-semibold text-[#b94a3a]">{error}</p>}
      {report && (
        <div className="rounded-[14px] border border-[#e5e8ef] bg-[#f9fafc] px-4 py-3 space-y-2">
          <p className="text-xs font-semibold text-[#16213e]">
            {report.dryRun ? "Preview (nothing sent)" : "Sent"} · {report.checked} learners checked ·{" "}
            {report.summaries} summaries · {report.alerts} alerts
            {report.rearmed ? ` · ${report.rearmed} caught up` : ""}
            {report.errors ? ` · ${report.errors} errors` : ""}
          </p>
          {report.log.length > 0 ? (
            <ul className="max-h-48 overflow-y-auto font-mono text-[12.5px] text-[#3b4560] space-y-0.5">
              {report.log.map((l, i) => (
                <li key={i}>{l}</li>
              ))}
            </ul>
          ) : (
            <p className="text-[12.5px] text-[#8189a0]">Nothing is due today.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Danger zone ────────────────────────────────────────────────── */
function DangerZone() {
  const me = useSelector((s: RootState) => s.adminAuth.user);
  const isSuperAdmin = me?.role === "super_admin";
  if (!isSuperAdmin) return null;

  return (
    <div className="rounded-[22px] border-2 border-[#f5d9d9] bg-white p-6 space-y-4">
      <div>
        <h3 className="text-base font-bold text-[#9b2a2a]">Danger zone</h3>
        <p className="text-xs text-[#8189a0] mt-0.5">
          Super admin actions that affect the entire platform.
        </p>
      </div>
      <div className="flex items-center justify-between rounded-[14px] border border-[#f5d9d9] bg-[#fff3f3] px-4 py-3">
        <div>
          <p className="text-xs font-bold text-[#9b2a2a]">Export all data</p>
          <p className="text-[11.5px] text-[#b07070]">
            Download a full data export of students, enrolments, and payments.
          </p>
        </div>
        <Button
          variant="danger"
          size="sm"
          onClick={() => alert("Data export — connect to backend endpoint.")}
        >
          Export
        </Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════ */
export default function AdminSettings() {
  useHashScroll();
  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Your account profile, password, and academy configuration."
      />
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <div id="profile" className="scroll-mt-24">
            <ProfileSection />
          </div>
          <div id="password" className="scroll-mt-24">
            <ChangePasswordSection />
          </div>
        </div>
        <div className="space-y-5">
          <PricingSection />
          <LearningJobSection />
          <AcademyInfoSection />
          <DangerZone />
        </div>
      </div>
    </>
  );
}
